"use client";

/**
 * Etapa opcional de vitrine.
 *
 * Produtos escolhidos ficam vinculados ao agendamento e são entregues no
 * fechamento do atendimento.
 */

import { FiShoppingBag } from "react-icons/fi";
import type { PublicProduct } from "../../../services/public";

interface BookingShowcaseStepProps {
  products: PublicProduct[];
  onSelectProduct: (product: PublicProduct) => void;
  onOpenAll: () => void;
}

function formatProductPrice(salePriceCents: number): string {
  return (Number(salePriceCents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export function BookingShowcaseStep({
  products,
  onSelectProduct,
  onOpenAll,
}: BookingShowcaseStepProps) {
  if (products.length === 0) return null;

  return (
    <section aria-label="Vitrine do estabelecimento" className="space-y-4">
      <div className="flex flex-col gap-3 rounded-2xl border border-primary-100 bg-primary-50/60 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white text-primary-700 ring-1 ring-primary-100">
            <FiShoppingBag className="h-5 w-5" aria-hidden="true" />
          </span>
          <p className="min-w-0 text-sm leading-6 text-slate-700">
            Escolher um produto é opcional. Ele fica reservado para você e é entregue no
            fechamento do atendimento.
          </p>
        </div>
        <button
          type="button"
          onClick={onOpenAll}
          className="inline-flex min-h-[2.75rem] flex-shrink-0 items-center justify-center rounded-xl border border-primary-200 bg-white px-5 py-2.5 text-sm font-semibold leading-none text-primary-700 transition hover:bg-primary-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
        >
          Ver vitrine completa
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product) => (
          <button
            key={product.id}
            type="button"
            onClick={() => onSelectProduct(product)}
            className="flex min-h-[92px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-card focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
          >
            <div className="h-[70px] w-[70px] flex-shrink-0 overflow-hidden rounded-xl bg-slate-100">
              {product.imageUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full items-center justify-center text-xs text-slate-400">
                  Sem foto
                </div>
              )}
            </div>
            <div className="min-w-0">
              <h3 className="line-clamp-2 text-sm font-semibold text-slate-900">
                {product.name}
              </h3>
              <p className="mt-0.5 text-sm font-semibold text-slate-800">
                {formatProductPrice(product.salePriceCents)}
              </p>
              <span
                className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                  product.available
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-rose-100 text-rose-700"
                }`}
              >
                {product.available ? "Disponível" : "Indisponível"}
              </span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
